/**
 * Pillar 2 — What You're Owed (BUILD-SPEC.md §4). Deterministic eligibility and
 * benefit math: the Federal Poverty Level (the foundation nearly every program
 * keys off), the Earned Income Tax Credit, and the Child Tax Credit. Each is a
 * pure function of the inputs and the bundled, cited dataset — no inference.
 */
import { Money } from "./money";
import type {
  AcaData,
  CitationData,
  EitcCtcData,
  FederalPovertyLevelData,
  FilingStatus,
  FplKeyedProgramsData,
  MedicaidData,
  SaversCreditData,
  SnapData,
} from "../data/schemas";

/** The poverty line for a household of `size` in the dataset's region (§4.1). */
export function povertyLine(size: number, fpl: FederalPovertyLevelData): Money {
  const people = Math.max(1, Math.floor(size));
  return Money.from(fpl.base).add(Money.from(fpl.perAdditionalPerson).multiply(people - 1));
}

/** Income as a percentage of the poverty line (e.g. 200 for 200% FPL). */
export function fplPercent(income: number, size: number, fpl: FederalPovertyLevelData): number {
  const line = povertyLine(size, fpl).toNumber();
  return line > 0 ? (Math.max(0, income) / line) * 100 : 0;
}

export interface EitcResult {
  /** Estimated credit. */
  credit: Money;
  /** The EITC bracket used (qualifying children, capped at 3+). */
  qualifyingChildren: number;
  /** True when income is past the point the credit fully phases out. */
  phasedOut: boolean;
  /**
   * True when §32(i) disallows the credit outright: investment income over the
   * year's limit ends it whatever the earnings are. A cutoff, not a phase-out,
   * so it is reported separately from {@link phasedOut}.
   */
  disqualifiedByInvestmentIncome: boolean;
  /** The §32(i) limit the investment income was measured against. */
  investmentIncomeLimit: Money;
}

/**
 * Estimate the Earned Income Tax Credit (§4.2). Phases in at `phaseInRate` up to
 * `maxCredit`, holds on a plateau, then phases out at `phaseOutRate` above the
 * filing-status threshold. Uses earned income as the income measure (the common
 * case; the statute uses the greater of earned income or AGI).
 *
 * §32(i) comes first: investment income over the year's limit disallows the
 * credit entirely, at any level of earnings, so a filer over that line gets zero
 * rather than the figure the phase-in would have produced.
 */
export function estimateEitc(
  input: {
    earnedIncome: number;
    qualifyingChildren: number;
    married: boolean;
    /** Aggregate §32(i) investment income. Absent means none. */
    investmentIncome?: number;
  },
  data: EitcCtcData,
): EitcResult {
  const qc = Math.max(0, Math.min(3, Math.floor(input.qualifyingChildren)));
  const limit = Money.from(data.disqualifyingInvestmentIncome);
  const disqualified =
    Math.max(0, input.investmentIncome ?? 0) > data.disqualifyingInvestmentIncome;
  const params = data.eitc.find((e) => e.qualifyingChildren === qc) ?? data.eitc[0];
  if (!params || disqualified) {
    return {
      credit: Money.zero(),
      qualifyingChildren: qc,
      phasedOut: false,
      disqualifiedByInvestmentIncome: disqualified,
      investmentIncomeLimit: limit,
    };
  }
  const income = Math.max(0, input.earnedIncome);
  const phaseIn = Math.min(params.maxCredit, income * params.phaseInRate);
  const threshold = input.married
    ? params.phaseOutThresholdMarried
    : params.phaseOutThresholdSingle;
  const reduction = Math.max(0, income - threshold) * params.phaseOutRate;
  const credit = Math.max(0, phaseIn - reduction);
  return {
    credit: Money.from(credit),
    qualifyingChildren: qc,
    phasedOut: income > 0 && credit === 0 && income > threshold,
    disqualifiedByInvestmentIncome: false,
    investmentIncomeLimit: limit,
  };
}

export interface CtcResult {
  /** Total Child Tax Credit after the high-income phaseout. */
  credit: Money;
  /** Refundable portion available even with no tax liability (the ACTC), capped. */
  refundable: Money;
  /**
   * True when §24(d)(1)(B)(i) — 15% of earned income over $2,500 — is what caps
   * the refundable portion, rather than the per-child cap. At low earnings this
   * is the binding rule and the difference is most of the money.
   */
  refundableLimitedByEarnedIncome: boolean;
}

/**
 * Estimate the Child Tax Credit and its refundable portion (§4.2). The credit is
 * `perChild` per qualifying child, reduced by `phaseOutPerThousand` for every
 * $1,000 (or fraction) of MAGI above the filing-status threshold.
 *
 * The refundable portion (the ACTC) is the smallest of three things: the credit
 * itself, the per-child refundable cap, and §24(d)(1)(B)(i)'s **15% of earned
 * income over $2,500**. That third limb is the one that binds at low earnings,
 * which is exactly where the refundable portion is the whole point — so
 * `earnedIncome` is asked for rather than assumed. Omitting it reports the
 * per-child cap, which is a ceiling and not an estimate.
 *
 * Not modeled: §24(d)(1)(B)(ii), the alternative for three or more qualifying
 * children (social security taxes over the earned income credit). It can only
 * raise the refundable amount, so this is a floor for those households.
 */
export function estimateCtc(
  input: {
    qualifyingChildren: number;
    magi: number;
    married: boolean;
    /** Earned income for §24(d)(1)(B)(i). Absent reports the per-child cap. */
    earnedIncome?: number;
  },
  data: EitcCtcData,
): CtcResult {
  const kids = Math.max(0, Math.floor(input.qualifyingChildren));
  const ctc = data.childTaxCredit;
  const base = ctc.perChild * kids;
  const threshold = input.married ? ctc.phaseOutThresholdMarried : ctc.phaseOutThresholdSingle;
  const excess = Math.max(0, Math.max(0, input.magi) - threshold);
  const steps = Math.ceil(excess / 1000);
  const reduction = steps * ctc.phaseOutPerThousand;
  const credit = Math.max(0, base - reduction);
  const capped = Math.min(credit, ctc.refundableCap * kids);
  const phaseIn =
    input.earnedIncome === undefined
      ? Infinity
      : Math.max(0, Math.max(0, input.earnedIncome) - ctc.refundableEarnedIncomeThreshold) *
        ctc.refundablePhaseInRate;
  const refundable = Math.min(capped, phaseIn);
  return {
    credit: Money.from(credit),
    refundable: Money.from(refundable),
    refundableLimitedByEarnedIncome: phaseIn < capped,
  };
}

export interface SaversCreditResult {
  /** Estimated non-refundable credit. */
  credit: Money;
  /** The credit rate that applied (0, 0.1, 0.2, or 0.5). */
  rate: number;
  /** Contributions actually counted, after the per-person cap. */
  eligibleContributions: Money;
}

/**
 * Estimate the Saver's Credit (§4.2). The credit rate steps down (50% → 20% →
 * 10% → 0) as AGI rises through the filing-status ceilings, applied to up to a
 * capped contribution amount. Non-refundable (it can only offset tax owed),
 * which the tile notes. Head of household uses its own AGI column; everyone
 * else uses the single column (the Form 8880 grouping).
 *
 * §25B(a) allows the credit on "the qualified retirement savings contributions
 * of the eligible individual ... as do not exceed $2,000" — **per individual**,
 * which is why Form 8880 has a column for you and a column for your spouse. A
 * joint return therefore caps each spouse at $2,000 separately, and $4,000 into
 * one spouse's account counts $2,000, not $4,000. `spouseContributions` is that
 * second column; on any other return it is ignored.
 */
export function estimateSaversCredit(
  input: {
    agi: number;
    filingStatus: FilingStatus;
    contributions: number;
    /** The other Form 8880 column. Only read on a joint return. */
    spouseContributions?: number;
  },
  data: SaversCreditData,
): SaversCreditResult {
  const agi = Math.max(0, input.agi);
  const married = input.filingStatus === "married_jointly";
  const capFor = (t: SaversCreditData["tiers"][number]): number =>
    married
      ? t.agiCapMarried
      : input.filingStatus === "head_of_household"
        ? t.agiCapHeadOfHousehold
        : t.agiCapSingle;

  // Tiers run highest-rate-first; take the best rate whose ceiling still covers
  // this AGI. Above the lowest tier's ceiling the rate is zero.
  let rate = 0;
  for (const tier of data.tiers) {
    if (agi <= capFor(tier)) {
      rate = tier.rate;
      break;
    }
  }

  const perPerson = data.maxContributionPerPerson;
  const own = Math.min(Math.max(0, input.contributions), perPerson);
  const spouse = married ? Math.min(Math.max(0, input.spouseContributions ?? 0), perPerson) : 0;
  const eligible = own + spouse;
  return {
    credit: Money.from(eligible).multiply(rate),
    rate,
    eligibleContributions: Money.from(eligible),
  };
}

export interface SnapResult {
  /** True when the household passes the tests that apply to it. */
  eligible: boolean;
  passedGrossTest: boolean;
  passedNetTest: boolean;
  /**
   * False for a household with an elderly or disabled member, which 7 CFR
   * §273.9(a) holds to the net standard only. Reported rather than folded into
   * `passedGrossTest`, so a surface can say the test does not apply instead of
   * showing a pass the household never had to earn.
   */
  grossTestApplies: boolean;
  /** Estimated monthly benefit (0 when ineligible). */
  monthlyBenefit: Money;
  grossMonthlyIncome: Money;
  /** The part the 20% deduction was taken on, after the cap at gross. */
  earnedMonthlyIncome: Money;
  netMonthlyIncome: Money;
  grossLimit: Money;
  netLimit: Money;
  maxAllotment: Money;
}

/** SNAP allotment / standard-deduction lookup, using the largest defined size
 *  for households beyond the table and adding the per-person amount past eight,
 *  up to the flat large-household allotment where the shard states one. */
function snapAllotment(size: number, data: SnapData): Money {
  const large = data.largeHouseholdAllotment;
  if (large && size >= large.fromHouseholdSize) return Money.from(large.amount);
  const sizes = Object.keys(data.maxAllotmentByHouseholdSize)
    .map(Number)
    .sort((a, b) => a - b);
  const top = sizes[sizes.length - 1] ?? 1;
  if (size <= top) {
    return Money.from(data.maxAllotmentByHouseholdSize[String(size)] ?? 0);
  }
  const base = data.maxAllotmentByHouseholdSize[String(top)] ?? 0;
  return Money.from(base).add(Money.from(data.additionalPersonAllotment).multiply(size - top));
}

function snapStandardDeduction(size: number, data: SnapData): Money {
  const sizes = Object.keys(data.standardDeductionByHouseholdSize)
    .map(Number)
    .sort((a, b) => a - b);
  const top = sizes[sizes.length - 1] ?? 1;
  const key = String(Math.min(size, top));
  return Money.from(data.standardDeductionByHouseholdSize[key] ?? 0);
}

/**
 * Estimate SNAP eligibility and the monthly benefit (§4.3). Applies the gross
 * income test (income ≤ a percentage of the poverty line), then the net income
 * test after the standard deduction and the earned-income deduction. The benefit
 * is the maximum allotment less the household's expected contribution (a share
 * of net income), floored at zero or the minimum benefit for small households.
 *
 * This is a deterministic estimate: it models the standard and earned-income
 * deductions but not the shelter, dependent-care, or medical deductions, which
 * would only raise the benefit. States vary; the agency decides.
 *
 * The elderly-or-disabled exemption is *modelled* rather than mentioned. 7 CFR
 * §273.9(a) is plain: "Households which contain an elderly or disabled member
 * shall meet the net income eligibility standards", and only households without
 * one "shall meet both". It sat in this comment and in the tile's explainer
 * while the result card said "Not eligible at this income" to a household the
 * gross test does not reach — a denial of food assistance, phrased with more
 * confidence than the rule has. Elderly means 60 or over (§271.2).
 *
 * Skipping the gross test can only ever reveal eligibility, never overstate a
 * benefit: the allotment still comes off the net test and the expected
 * contribution, both unchanged.
 */
export function estimateSnap(
  input: {
    householdSize: number;
    monthlyGrossIncome: number;
    /**
     * The part of that income that is EARNED — wages and self-employment.
     *
     * Required, and not defaulted to the gross figure. 7 CFR §273.9(d)(2)
     * allows the 20% deduction against earned income only, and defaulting it
     * to gross handed the deduction to a household living on Social Security
     * or SSI: a lower net income, a net test that could flip, and a larger
     * allotment, in the one direction this engine is not allowed to err. No
     * caller ever passed it, so every shipped surface was that household.
     */
    monthlyEarnedIncome: number;
    /** A member aged 60+ or with a disability, per 7 CFR §273.9(a) and §271.2. */
    elderlyOrDisabled?: boolean;
  },
  data: SnapData,
  fpl: FederalPovertyLevelData,
): SnapResult {
  const size = Math.max(1, Math.floor(input.householdSize));
  const gross = Money.from(Math.max(0, input.monthlyGrossIncome));
  // Never more than the gross: the deduction is a share of income the household
  // actually has, whatever two figures a crafted link carries.
  const earnedNum = Math.min(Math.max(0, input.monthlyEarnedIncome), gross.toNumber());
  const earned = Money.from(earnedNum);

  const monthlyLine = povertyLine(size, fpl).divide(12);
  const grossLimit = monthlyLine.multiply(data.grossIncomeLimitPctFpl / 100);
  const netLimit = monthlyLine.multiply(data.netIncomeLimitPctFpl / 100);

  const standardDeduction = snapStandardDeduction(size, data);
  const earnedDeduction = earned.multiply(data.earnedIncomeDeductionRate);
  let net = gross.subtract(standardDeduction).subtract(earnedDeduction);
  if (net.isNegative()) net = Money.zero();

  const grossTestApplies = !input.elderlyOrDisabled;
  const passedGrossTest = gross.lessThanOrEqual(grossLimit);
  const passedNetTest = net.lessThanOrEqual(netLimit);
  const eligible = (passedGrossTest || !grossTestApplies) && passedNetTest;

  const maxAllotment = snapAllotment(size, data);
  let monthlyBenefit = Money.zero();
  if (eligible) {
    const contribution = net.multiply(data.expectedContributionRate);
    monthlyBenefit = maxAllotment.subtract(contribution);
    if (monthlyBenefit.isNegative()) monthlyBenefit = Money.zero();
    // Minimum benefit floors eligible one- and two-person households.
    if (size <= 2 && monthlyBenefit.lessThan(data.minBenefit)) {
      monthlyBenefit = Money.from(data.minBenefit);
    }
  }

  return {
    eligible,
    passedGrossTest,
    passedNetTest,
    grossTestApplies,
    monthlyBenefit,
    grossMonthlyIncome: gross,
    earnedMonthlyIncome: earned,
    netMonthlyIncome: net,
    grossLimit,
    netLimit,
    maxAllotment,
  };
}

export interface MedicaidResult {
  /** Whether the state expanded Medicaid under the ACA. */
  expansionState: boolean;
  /** The MAGI eligibility ceiling as a % of FPL, or null in non-expansion states. */
  thresholdPctFpl: number | null;
  /** Likely eligible / not, or null when non-expansion (can't be determined simply). */
  eligible: boolean | null;
  /** Household income as a percentage of the poverty line. */
  fplPercent: number;
}

/**
 * Adult Medicaid eligibility by state (§4.3). In an expansion state, an adult at
 * or below the threshold (138% FPL, or a state override) is likely eligible. In a
 * non-expansion state, adult coverage is limited and category-specific (parents,
 * pregnancy, disability), so we return null eligibility and let the tile say so
 * rather than invent a precise number.
 */
export function medicaidEligibility(
  input: { stateCode: string; income: number; householdSize: number },
  data: MedicaidData,
  fpl: FederalPovertyLevelData,
): MedicaidResult {
  const code = input.stateCode.toUpperCase();
  const expanded = data.expansionByState[code] ?? false;
  const pct = fplPercent(input.income, input.householdSize, fpl);
  if (!expanded) {
    return { expansionState: false, thresholdPctFpl: null, eligible: null, fplPercent: pct };
  }
  const threshold = data.thresholdOverridesPctFpl?.[code] ?? data.expansionThresholdPctFpl;
  return {
    expansionState: true,
    thresholdPctFpl: threshold,
    eligible: pct <= threshold,
    fplPercent: pct,
  };
}

/**
 * Whether an exact FPL% falls inside a band. Interior bands are half-open — the
 * table reads "133% up to 150%", and the next band's initial percentage equals
 * this one's final, so the shared endpoint belongs to the band above it and the
 * schedule stays continuous. The *last* closed band is the exception: §36B(c)(1)(A)
 * makes an applicable taxpayer one whose income "does not exceed 400 percent" of
 * the poverty line, so a household standing exactly on 400% is inside the table,
 * not past it. Closing that endpoint is the difference between the last band and
 * the cliff.
 */
function inBand(fplPct: number, band: AcaData["applicablePercentage"][number], last: boolean) {
  if (band.fplHigh === null) return fplPct >= band.fplLow;
  return fplPct >= band.fplLow && (fplPct < band.fplHigh || (last && fplPct === band.fplHigh));
}

/** The applicable percentage for an exact FPL%, interpolated within its band. */
export function acaApplicablePercent(fplPct: number, data: AcaData): number {
  const bands = data.applicablePercentage;
  for (let i = 0; i < bands.length; i++) {
    const band = bands[i]!;
    if (!inBand(fplPct, band, i === bands.length - 1)) continue;
    if (band.fplHigh === null) return band.percentageHigh;
    const span = band.fplHigh - band.fplLow;
    const frac = span > 0 ? (fplPct - band.fplLow) / span : 0;
    return band.percentageLow + (band.percentageHigh - band.percentageLow) * frac;
  }
  return 0;
}

/**
 * Whether an exact FPL% falls within a credit-eligible band. With the post-2025
 * table the top band ends at 400% FPL (the subsidy cliff returns), so income
 * *above* it is not covered and earns no credit — but income exactly *on* it is
 * covered, per §36B(c)(1)(A)'s "does not exceed 400 percent". An open-ended top
 * band (the ARPA-enhanced schedule) instead covers everything above its floor.
 */
export function acaCovered(fplPct: number, data: AcaData): boolean {
  const bands = data.applicablePercentage;
  return bands.some((band, i) => inBand(fplPct, band, i === bands.length - 1));
}

/**
 * Whether the premium tax credit reaches this FPL% at all — the whole of
 * §36B(c)(1)(A)'s "equals or exceeds 100 percent but does not exceed 400
 * percent", rather than the half of it each caller used to write for itself.
 *
 * `acaCovered` answers a narrower question: whether the applicable-percentage
 * table has a band for this income. The table starts at 0%, so it says yes to a
 * household at 50% FPL, which is true of the table and false of the credit. The
 * Readout Report asked `acaCovered` and told such a household it was likely
 * eligible; the Benefit Cliff Explorer asked `eligible` and plotted zero at the
 * same income. Both were reading this engine.
 */
export function acaCreditEligible(fplPct: number, data: AcaData): boolean {
  return fplPct >= 100 && acaCovered(fplPct, data);
}

export interface AcaResult {
  /** Household income as a percentage of the poverty line. */
  fplPercent: number;
  /** Applicable percentage of income expected toward the benchmark plan. */
  applicablePercent: number;
  /** Annual income expected to go toward the benchmark plan. */
  expectedAnnualContribution: Money;
  /** Monthly version of the expected contribution. */
  expectedMonthlyContribution: Money;
  /** Estimated monthly premium tax credit (≥ 0). */
  monthlyCredit: Money;
  /** Estimated annual premium tax credit (≥ 0). */
  annualCredit: Money;
  /** True when a credit is available (income ≥ 100% FPL and the benchmark exceeds the contribution). */
  eligible: boolean;
  /** True when income is below 100% FPL (Medicaid territory in expansion states). */
  belowMedicaidFloor: boolean;
  /** True when income is above the top subsidy band (the 400% FPL cliff, restored for 2026). */
  aboveSubsidyCap: boolean;
}

/**
 * Estimate the ACA premium tax credit (§4.2). The credit is the benchmark
 * (second-lowest-cost silver) plan premium minus the household's expected
 * contribution — income times the applicable percentage for its FPL band. For
 * plan year 2026 the ARPA/IRA-enhanced subsidies have expired, so the table
 * reverts to the higher applicable percentages and the 400%-FPL cliff returns:
 * above 400% FPL there is no credit. The benchmark premium is per-county, so the
 * user supplies it (from HealthCare.gov); we ship the cited applicable-percentage
 * table and compute the rest deterministically.
 */
export function estimatePremiumTaxCredit(
  input: { householdSize: number; annualIncome: number; benchmarkMonthlyPremium: number },
  aca: AcaData,
  fpl: FederalPovertyLevelData,
): AcaResult {
  const pct = fplPercent(input.annualIncome, input.householdSize, fpl);
  const belowMedicaidFloor = pct < 100;
  // Above the top band (the 400% FPL cliff, restored for plan year 2026 once the
  // enhanced subsidies expired) there is no premium tax credit at all.
  const aboveSubsidyCap = pct >= 100 && !acaCovered(pct, aca);
  const applicablePercent = acaApplicablePercent(pct, aca);
  const expectedAnnual = Money.from(Math.max(0, input.annualIncome)).multiply(
    applicablePercent / 100,
  );
  const benchmarkAnnual = Money.from(Math.max(0, input.benchmarkMonthlyPremium)).multiply(12);
  const rawCredit = benchmarkAnnual.subtract(expectedAnnual);
  // Both ends of the band bar the credit, and this used to zero only one of
  // them. §36B(c)(1)(A) requires household income that "equals or exceeds 100
  // percent" of the poverty line as much as it requires "not more than 400
  // percent", so a household under the floor is no more entitled to a credit
  // than one over the cap — it belongs to Medicaid in an expansion state and to
  // the coverage gap in the rest. Returning a figure for it made the ACA tile
  // headline thousands of dollars at an income where the Benefit Cliff Explorer,
  // reading the same function, plotted zero, because the sweep knew to gate on
  // `eligible` and the tile printed `annualCredit`.
  const barred = aboveSubsidyCap || belowMedicaidFloor;
  const annualCredit = barred || rawCredit.isNegative() ? Money.zero() : rawCredit;
  return {
    fplPercent: pct,
    applicablePercent,
    expectedAnnualContribution: expectedAnnual,
    expectedMonthlyContribution: expectedAnnual.divide(12),
    monthlyCredit: annualCredit.divide(12),
    annualCredit,
    eligible: pct >= 100 && !aboveSubsidyCap && annualCredit.greaterThan(0),
    belowMedicaidFloor,
    aboveSubsidyCap,
  };
}

/** A program keyed to the poverty line that the household appears to be under (SPEC-5 F4). */
export interface FplKeyedRow {
  id: string;
  name: string;
  worth: string;
  categorical: string;
  agencyUrl: string;
  rule: CitationData;
  /** The line the household is at or under, as a percentage; null where each state sets it. */
  linePct: number | null;
  /** School meals: free under the first line, reduced-price under the second. */
  reducedPrice: boolean;
}

/**
 * The programs whose federal line is a percentage of the poverty guidelines,
 * filtered to the household. Each is a reason to apply, never a determination:
 * every one of them also admits people through other programs, and the
 * agency decides. A program a state sets the line for is listed only while the
 * household is under the federal ceiling's poverty-line leg, and never with a
 * line of its own.
 */
export function fplKeyedRows(
  pctOfLine: number,
  household: { children: number; youngChild: boolean },
  data: FplKeyedProgramsData,
): FplKeyedRow[] {
  const rows: FplKeyedRow[] = [];
  for (const p of data.programs) {
    if (p.requires === "children" && household.children <= 0) continue;
    if (p.requires === "youngChild" && !household.youngChild) continue;
    const row = {
      id: p.id,
      name: p.name,
      worth: p.worth,
      categorical: p.categorical,
      agencyUrl: p.agencyUrl,
      rule: p.rule,
    };
    if (p.pctFpl === null) {
      if (p.ceilingPctFpl === undefined || pctOfLine <= p.ceilingPctFpl)
        rows.push({ ...row, linePct: null, reducedPrice: false });
    } else if (pctOfLine <= p.pctFpl) {
      rows.push({ ...row, linePct: p.pctFpl, reducedPrice: false });
    } else if (p.reducedPctFpl !== undefined && pctOfLine <= p.reducedPctFpl) {
      rows.push({ ...row, linePct: p.reducedPctFpl, reducedPrice: true });
    }
  }
  return rows;
}
